import { useEffect } from 'react';
import type { InvestigationResult, InvestigationStat } from '@soc/shared';
import { useInvestigateMutation } from '../../app/api';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useSelectedProject } from '../projects/useSelectedProject';
import { sevChip, sevColor } from '../../lib/severity';
import { RuleIcon } from '../../lib/ruleIcons';
import { apiErrorMessage } from '../../lib/validation';
import { closeInvestigation, type InvestigationTarget } from './investigationSlice';
import { useInvestigationHover } from './investigationHover';

/**
 * The right sidebar of Investigate Mode. Reads `investigation.active`, fires the
 * `investigate` mutation once per target (aborting a stale one when the target
 * changes), and renders the render-agnostic result: header · Site/Summary stats ·
 * raw-rows table · legend. ONE code path for every rule — the data drives it.
 */
export function InvestigationPanel() {
  const dispatch = useAppDispatch();
  const target = useAppSelector((s) => s.investigation.active);
  const project = useSelectedProject();
  // Shared cache key so the map layer (CellMap) reads the SAME result without a
  // second request — one fetch feeds both the panel and the evidence layer.
  const [investigate, { data, isLoading, error, reset }] = useInvestigateMutation({
    fixedCacheKey: 'investigation',
  });

  // Fire once per target; abort + reset when the target changes or the panel closes.
  useEffect(() => {
    if (!target) return;
    const req = investigate({
      ruleId: target.ruleId,
      towerId: target.towerId,
      from: target.from,
      to: target.to,
      params: target.params,
      projectId: project?.id,
      polygon: project?.polygon,
    });
    return () => {
      req.abort();
      reset();
    };
    // Key on anomalyId — reopening the same anomaly reuses nothing new; a different
    // anomaly refires. project id/polygon are stable within a session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.anomalyId]);

  if (!target) return null;

  return (
    <aside
      className="theme-noc"
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface-card)',
        borderLeft: '1px solid var(--border-hover)',
        minHeight: 0,
      }}
    >
      <Header
        target={target}
        result={data}
        onClose={() => dispatch(closeInvestigation())}
      />
      <div className="scrl" style={{ overflowY: 'auto', minHeight: 0, padding: '10px 14px 20px' }}>
        {isLoading && <Muted>Investigating…</Muted>}
        {error && <ErrorBox>{apiErrorMessage(error, 'Investigation failed')}</ErrorBox>}
        {data && <Body result={data} />}
      </div>
    </aside>
  );
}

function Header({
  target,
  result,
  onClose,
}: {
  target: InvestigationTarget;
  result?: InvestigationResult;
  onClose: () => void;
}) {
  const severity = result?.header.severity;
  const color = severity ? sevColor(severity) : 'var(--text-primary)';
  return (
    <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ display: 'inline-flex', color }}>
          <RuleIcon ruleId={target.ruleId} size={16} />
        </span>
        {severity && (
          <span
            className="eyebrow"
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.8,
              color,
              background: sevChip(severity),
              padding: '2px 6px',
              borderRadius: 4,
              textTransform: 'uppercase',
            }}
          >
            {severity}
          </span>
        )}
        <span style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0 }}>
          {result?.header.ruleName ?? 'Investigating…'}
        </span>
        <button
          type="button"
          aria-label="Close investigation"
          title="Close (Esc)"
          onClick={onClose}
          style={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: '1px solid var(--border-hover)',
            borderRadius: 6,
            color: 'var(--text-muted)',
            cursor: 'pointer',
          }}
        >
          ✕
        </button>
      </div>
      <div className="mono" style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, wordBreak: 'break-word' }}>
        {target.towerId}
      </div>
      {result && (
        <div className="mono" style={{ fontSize: 13, color, marginTop: 2 }}>
          {result.header.value}
        </div>
      )}
    </div>
  );
}

function Body({ result }: { result: InvestigationResult }) {
  const site = result.stats.filter((s) => s.group === 'site');
  const summary = result.stats.filter((s) => s.group !== 'site');
  return (
    <>
      {site.length > 0 && (
        <Section title="Site">
          {site.map((s, i) => (
            <StatRow key={i} stat={s} />
          ))}
        </Section>
      )}
      <Section title="Summary">
        {summary.map((s, i) => (
          <StatRow key={i} stat={s} />
        ))}
      </Section>
      <Section title="Raw rows">
        <RowsTable result={result} />
      </Section>
      {result.legend.length > 0 && (
        <Section title="Legend">
          {result.legend.map((l, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0', fontSize: 12 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  flex: 'none',
                  borderRadius: l.symbol === 'diamond' ? 2 : '50%',
                  background: l.symbol === 'ring' ? 'transparent' : l.color,
                  border: l.symbol === 'ring' ? `2px solid ${l.color}` : undefined,
                  boxShadow: `0 0 6px ${l.color}`,
                  transform: l.symbol === 'diamond' ? 'rotate(45deg)' : undefined,
                }}
              />
              <span style={{ color: 'var(--text-secondary)' }}>{l.label}</span>
            </div>
          ))}
        </Section>
      )}
    </>
  );
}

function RowsTable({ result }: { result: InvestigationResult }) {
  const { hovered, setHovered } = useInvestigationHover();
  const { columns, rows, cellColors } = result.table;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="mono" style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
        <thead>
          <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
            {columns.map((c) => (
              <th key={c.key} style={{ padding: '3px 6px', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap' }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const rowRef = row.rowRef != null ? String(row.rowRef) : String(i);
            const isHi = rowRef === hovered;
            return (
              <tr
                key={i}
                onMouseEnter={() => setHovered(rowRef)}
                onMouseLeave={() => setHovered(null)}
                style={{ background: isHi ? 'rgba(0,229,255,0.14)' : undefined }}
              >
                {columns.map((c) => {
                  const cc = cellColors?.[`${i}:${c.key}`];
                  return (
                    <td key={c.key} style={{ padding: '3px 6px', whiteSpace: 'nowrap', color: cc }}>
                      {row[c.key] ?? '—'}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div
        className="eyebrow"
        style={{ fontSize: 10, letterSpacing: 0.8, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function StatRow({ stat }: { stat: InvestigationStat }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12, padding: '2.5px 0' }}>
      <span style={{ flex: 1, minWidth: 0, color: 'var(--text-muted)' }}>{stat.label}</span>
      <span className="mono" style={{ color: stat.color ?? 'var(--text-secondary)', wordBreak: 'break-word', textAlign: 'right' }}>
        {stat.value}
      </span>
    </div>
  );
}

const Muted = ({ children }: { children: React.ReactNode }) => (
  <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>{children}</div>
);
const ErrorBox = ({ children }: { children: React.ReactNode }) => (
  <div style={{ color: 'var(--danger, #ff6b6b)', fontSize: 12, padding: '14px 0' }}>{children}</div>
);
